package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.viewmodels.AvailabilitySharingViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.availability_sharing_ask_body
import evoshift.composeapp.generated.resources.availability_sharing_ask_no
import evoshift.composeapp.generated.resources.availability_sharing_ask_title
import evoshift.composeapp.generated.resources.availability_sharing_ask_yes
import evoshift.composeapp.generated.resources.availability_sharing_desc
import evoshift.composeapp.generated.resources.availability_sharing_title
import org.jetbrains.compose.resources.stringResource

object AvailabilitySharingTags {
    const val SWITCH = "availability_sharing_switch"
}

/**
 * SH-135, Profile: "Share my availability with all employers", for someone working for two or more
 * employers (nothing to choose otherwise: the same owner's workplaces always share it).
 */
@Composable
fun AvailabilitySharingCard(viewModel: AvailabilitySharingViewModel, modifier: Modifier = Modifier) {
    val state by viewModel.state.collectAsState()
    if (!state.offersChoice) return
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .toggleable(value = state.shareWithAll, role = Role.Switch, onValueChange = viewModel::set)
            .testTag(AvailabilitySharingTags.SWITCH)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f)) {
            Text(stringResource(Res.string.availability_sharing_title), style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Medium)
            Text(
                stringResource(Res.string.availability_sharing_desc),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Spacer(Modifier.width(12.dp))
        Switch(checked = state.shareWithAll, onCheckedChange = null)
    }
}

/**
 * SH-135: asked once, when the person works for a second employer and opens their availability:
 * whether the other employers see it too. Either answer is remembered; Profile changes it later.
 */
@Composable
fun AvailabilitySharingQuestion(viewModel: AvailabilitySharingViewModel) {
    val state by viewModel.state.collectAsState()
    if (!state.shouldAsk) return
    EvoDialog(
        title = stringResource(Res.string.availability_sharing_ask_title),
        icon = Icons.Default.Share,
        onDismiss = { viewModel.set(false) },
        dismissible = false,
        confirm = DialogAction(stringResource(Res.string.availability_sharing_ask_yes), { viewModel.set(true) }),
        dismiss = DialogAction(stringResource(Res.string.availability_sharing_ask_no), { viewModel.set(false) })
    ) {
        Text(stringResource(Res.string.availability_sharing_ask_body), style = MaterialTheme.typography.bodyMedium)
    }
}
